import { useEffect, useState } from 'react';
import { useScraperSource } from '../../../api/queries';
import { useSaveScraperSource } from '../../../api/mutations';
import { Ic } from '../../../components/Ic';

/** Replaces both the standalone /editor page and the duplicate editor modal in
 *  the old dashboard — they hit these same two endpoints.
 *
 *  A styled textarea for now; the old editor was an unstyled textarea with no
 *  highlighting, so this is already a step up. CodeMirror can be lazy-loaded
 *  here later without touching anything else. */
export function CodeTab({ name }: { name: string }) {
  const { data, isLoading, error } = useScraperSource(name);
  const save = useSaveScraperSource();
  const [draft, setDraft] = useState<string | null>(null);

  // Reset the draft when the file loads or the scraper changes, so an edit to
  // one scraper can never be saved over another.
  useEffect(() => setDraft(null), [name]);

  const content = draft ?? data?.content ?? '';
  const dirty = draft !== null && draft !== data?.content;

  if (error) {
    return (
      <div className="err">
        No source file for this scraper. Template-based scrapers have no .py to edit.
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span className="mono-sm muted">{data?.name ? `${data.name}.py` : ''}</span>
        <span style={{ flex: 1 }} />
        {dirty && <span className="pill yellow">unsaved</span>}
        <button
          className="btn primary sm"
          disabled={!dirty || save.isPending}
          onClick={() => save.mutate({ name, content })}
        >
          <Ic.check className="ico" /> {save.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>

      <textarea
        className="input code"
        spellCheck={false}
        value={isLoading ? 'Loading…' : content}
        disabled={isLoading}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if ((e.ctrlKey || e.metaKey) && e.key === 's') {
            e.preventDefault();
            if (dirty) save.mutate({ name, content });
          }
        }}
        style={{ minHeight: 380, whiteSpace: 'pre', overflowWrap: 'normal', overflowX: 'auto' }}
      />
      <div className="muted" style={{ fontSize: 11.5 }}>
        <span className="kbd">Ctrl</span> + <span className="kbd">S</span> to save. Changes take
        effect on the next scheduled run.
      </div>
    </div>
  );
}
